/**
 * MobileNav — slide-over navigation panel for mobile.
 *
 * Auth-aware: shows Login for guests, avatar + logout for
 * authenticated users. CTA adapts to creator profile status.
 */

import { useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { NAV_ITEMS } from '@/config/navigation';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useAuth } from '@/hooks/auth';
import { useCreatorProfile } from '@/hooks/useCreatorProfile';
import { analyticsService } from '@/lib/AnalyticsService';
import { Avatar } from '@/components/ui/Avatar';
import { BrandLogo } from './BrandLogo';

export interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNav({ isOpen, onClose }: MobileNavProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();
  const { action } = useCreatorProfile();

  useScrollLock(isOpen);
  useFocusTrap(panelRef, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'rounded-md px-3 py-3 text-sm font-medium tracking-tight transition-colors duration-fast ease-standard',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15',
      isActive
        ? 'bg-neutral-100 text-foreground'
        : 'text-muted-foreground hover:bg-neutral-50 hover:text-foreground',
    );

  const cta = action ?? { label: 'Join the Creator Registry', path: '/apply' };

  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden" id="mobile-nav">
      {/* Backdrop */}
      <div
        className="absolute inset-0 h-full w-full bg-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="absolute right-0 top-0 flex h-full w-[320px] max-w-[85vw] flex-col bg-surface shadow-xl animate-slide-in-right"
      >
        {/* Panel header */}
        <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-border px-6">
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="UNIVIBE home"
          >
            <BrandLogo className="h-4 w-auto shrink-0 sm:h-5" />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground',
              'transition-colors duration-fast ease-standard hover:bg-neutral-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Navigation items */}
        <nav className="flex flex-col gap-1 overflow-y-auto p-6" aria-label="Mobile">
          {/* User info (when authenticated) */}
          {user && (
            <div className="mb-4 flex items-center gap-3 rounded-md bg-neutral-50 p-3">
              <Avatar name={user.fullName || user.email} size="md" />
              <div className="flex flex-col overflow-hidden">
                <span className="truncate text-sm font-medium text-foreground">
                  {user.fullName || user.email}
                </span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </div>
            </div>
          )}

          {NAV_ITEMS.map((item) => (
            <NavLink key={item.path} to={item.path} onClick={onClose} className={mobileLinkClass}>
              {item.label}
            </NavLink>
          ))}

          {user ? (
            <>
              <NavLink to="/workspace" onClick={onClose} className={mobileLinkClass}>
                My Workspace
              </NavLink>
              <NavLink to="/dashboard" onClick={onClose} className={mobileLinkClass}>
                Creator Dashboard
              </NavLink>
              <div className="my-3 h-px bg-border" />
              <button
                type="button"
                onClick={() => {
                  void signOut();
                  onClose();
                }}
                className={cn(
                  'rounded-md px-3 py-3 text-left text-sm font-medium text-muted-foreground',
                  'transition-colors hover:bg-neutral-50 hover:text-foreground',
                )}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <div className="my-3 h-px bg-border" />
              <NavLink to="/login" onClick={onClose} className={mobileLinkClass}>
                Login
              </NavLink>
            </>
          )}

          {/* Status-based CTA */}
          <Link
            to={cta.path}
            onClick={() => { analyticsService.track('registration_cta_clicked', { location: 'mobile_nav' }); onClose(); }}
            className={cn(
              'mt-4 inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-medium',
              'bg-primary-700 text-primary-foreground',
              'transition-all duration-fast ease-standard hover:bg-primary-800 active:bg-primary-900',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {cta.label}
          </Link>
        </nav>
      </div>
    </div>,
    document.body,
  );
}
